import React, { Fragment, useRef, useState, useEffect } from "react";
import { Grid, Divider, useTheme } from "@mui/material";
import { IconButton } from "@mui/material";
import {
  CloseRounded,
  AddRounded,
  RemoveRounded,
  FullscreenRounded,
} from "@mui/icons-material";
import { Typography, Box } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import ImageWrapper from "./ImageWrapper";
import PIDMarkupFullScreen from "./PIDMarkupFullScreen";

const useStyles = makeStyles()((theme) => ({
  iconButton: {
    color: theme.palette.text.primary,
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

export const PID_IMAGE_ID = "pid-image";

function PIDMarkup({
  lastStreamedMessageId,
  isolationMarkup,
  onIsolationMarkupChange,
  selectedIsolationMarkup,
  onSelectedIsolationMarkupChange,
  onClose,
}) {
  const theme = useTheme();
  const [scale, setScale] = useState(4);
  const transformWrapperRef = useRef(null);
  const { classes } = useStyles();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [openFullScreen, setOpenFullScreen] = useState(false);
  const [annotations, setAnnotations] = useState(isolationMarkup?.annotations || []);

  // Progressively load annotations with 1-second delay
  // useEffect(() => {
  //   if (lastStreamedMessageId) {
  //     if (isolationMarkup?.annotations) {
  //       setAnnotations([]); // Reset annotations when selectedPIDMarkups changes

  //       const sourceAnnotations = isolationMarkup.annotations;
  //       let currentIndex = 0;

  //       const intervalId = setInterval(() => {
  //         if (currentIndex < sourceAnnotations.length) {
  //           setAnnotations((prev) => [
  //             ...prev,
  //             sourceAnnotations[currentIndex],
  //           ]);
  //           onSelectedIsolationMarkupChange(sourceAnnotations[currentIndex]);
  //           currentIndex++;
  //         } else {
  //           clearInterval(intervalId);
  //         }
  //       }, 1000); // 1 second delay

  //       return () => clearInterval(intervalId); // Cleanup on unmount or when selectedPIDMarkups changes
  //     } else {
  //       setAnnotations([]);
  //     }
  //   } else {
  //     setAnnotations(isolationMarkup?.annotations || []);
  //   }
  // }, [isolationMarkup, lastStreamedMessageId]);

  const handleZoomIn = () => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.zoomOut();
    }
  };

  useEffect(() => {
    if (
      isolationMarkup &&
      selectedIsolationMarkup &&
      transformWrapperRef.current &&
      isImageLoaded
    ) {
      const { setTransform } = transformWrapperRef.current;
      const { wrapperComponent } = transformWrapperRef.current.instance;
      const imageElement = document.getElementById(PID_IMAGE_ID);

      if (wrapperComponent && imageElement) {
        const { clientWidth, clientHeight } = wrapperComponent;
        const currentScale = scale;

        const {
          naturalWidth,
          naturalHeight,
          width: renderedWidth,
          height: renderedHeight,
        } = imageElement;

        const renderScale = renderedWidth / naturalWidth;

        const selectedAnnotationX = parseFloat(selectedIsolationMarkup.x);
        const selectedAnnotationY = parseFloat(selectedIsolationMarkup.y);

        const annotationWidth = selectedIsolationMarkup.template_width || 0;
        const annotationHeight = selectedIsolationMarkup.template_height || 0;

        const annotationNaturalCenterX =
          selectedAnnotationX + annotationWidth / 2;
        const annotationNaturalCenterY =
          selectedAnnotationY + annotationHeight / 2;

        const contentDiv = imageElement.parentElement.parentElement;
        const offsetX = (contentDiv.clientWidth - renderedWidth) / 2;
        const offsetY = (contentDiv.clientHeight - renderedHeight) / 2;

        const annotationRenderedCenterX =
          annotationNaturalCenterX * renderScale + offsetX;
        const annotationRenderedCenterY =
          annotationNaturalCenterY * renderScale + offsetY;

        const newX = clientWidth / 2 - annotationRenderedCenterX * currentScale;
        const newY =
          clientHeight / 2 - annotationRenderedCenterY * currentScale;

        setTransform(newX, newY, currentScale, 300, "easeOut");
      }
    }
  }, [isImageLoaded, selectedIsolationMarkup]);

  return (
    <Grid
      item
      size={{ md: 5 }}
      height={"100%"}
      sx={{
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
      }}>
      <Box
        px={2}
        py={1}
        display={"flex"}
        alignItems="center"
        justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={onClose} className={classes.iconButton}>
            <CloseRounded />
          </IconButton>
          <Typography variant="h6">PID Markup</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box display="flex" alignItems="center" gap={1} mr={2}>
            <Typography variant="body2">{scale.toFixed(1)}x</Typography>
            <IconButton
              onClick={handleZoomIn}
              size="small"
              disabled={scale >= 16}
              className={classes.iconButton}>
              <AddRounded />
            </IconButton>
            <IconButton
              onClick={handleZoomOut}
              size="small"
              disabled={scale <= 1}
              className={classes.iconButton}>
              <RemoveRounded fontSize="small" />
            </IconButton>
          </Box>
          <IconButton
            onClick={() => setOpenFullScreen(true)}
            className={classes.iconButton}>
            <FullscreenRounded />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <Box flex={1} overflow={"hidden"}>
        <TransformWrapper
          initialScale={scale}
          minScale={1}
          maxScale={16}
          wheel={{ step: 0.5 }}
          pinch={{ step: 0.6 }}
          centerOnInit={true}
          onTransformed={(_, state) => {
            setScale(state.scale);
          }}
          ref={transformWrapperRef}>
          <TransformComponent
            wrapperStyle={{
              width: "100%",
              height: "100%",
              margin: "auto",
            }}
            contentStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <ImageWrapper
              selectedIsolationMarkup={selectedIsolationMarkup}
              annotations={annotations}
              scale={scale}
              PID_IMAGE_ID={PID_IMAGE_ID}>
              <img
                id={PID_IMAGE_ID}
                src={isolationMarkup.pid_url}
                alt="PID Diagram"
                style={{
                  maxWidth: "100vw",
                  maxHeight: "100vh",
                  objectFit: "contain",
                }}
                // crossOrigin="anonymous"
                onLoad={() => {
                  setIsImageLoaded(true);
                }}
              />
            </ImageWrapper>
          </TransformComponent>
        </TransformWrapper>
      </Box>
      {openFullScreen && (
        <PIDMarkupFullScreen
          pid_url={isolationMarkup.pid_url}
          annotations={annotations}
          onClose={() => setOpenFullScreen(false)}
        />
      )}
    </Grid>
  );
}

export default PIDMarkup;
